import { HttpParams } from '@angular/common/http'
import { Component, OnDestroy, effect, input } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faFireFlameCurved, faRepeat, faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellType, DataMap, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'
import { QuillViewComponent } from 'ngx-quill'
import { Subscription } from 'rxjs'
import { MeetingEntityTypes } from 'src/app/shared/services/meeting-hub.service'
import { getInitials } from 'src/app/shared/shared-functions'

import { BaseComponent, FeatureType, Grid, ListItem, Meeting, ModalBackDrop, ModalSize, ToDo, User } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { MeetingItemsSelectionComponent } from '../meeting-items-selection/meeting-items-selection.component'
import { ToDoComponent } from './to-do.component'

@Component({
    selector: 'app-to-dos',
    templateUrl: './to-dos.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        WjInputModule,
        QuillViewComponent,
        FaIconComponent,
    ],
})
export class ToDosComponent extends BaseComponent implements OnDestroy {
    faFire = faFireFlameCurved
    faRepeat = faRepeat
    faSearch = faSearch

    private weekOfMonthLabels = ['1st', '2nd', '3rd', '4th', 'Last']
    private dayOfWeekLabels = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

    meetingId = input.required<string>()
    meetings = input.required<Meeting[]>()
    meeting = input.required<Meeting>()

    allUsers: User[] = []
    allUserMap!: DataMap
    allActiveUsers: User[] = []
    allActiveUserList: ListItem<string>[] = []

    isUserToDos = false

    canViewAdmin = true

    grid: Grid<ToDo>

    private contentSyncSub?: Subscription
    private isCellEditing = false
    private pendingContentSync = false

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.MeetingsMeetingView])
    }

    constructor() {
        super()

        effect(() => {
            if (this.meeting()) {
                this.refreshTodos()
            }
        })

        this.grid = new Grid<ToDo>(this.services)
            .setupModalEditing(this.services.data.toDos, 'To-Do', ToDoComponent, () => new ToDo(), this, ModalSize.medium)
            .filterBy(item => [item.name])
            .isReadOnly(false)

        this.grid.itemAdded.on(this.handleItemAdded.bind(this))

        this.contentSyncSub = this.services.meetingContentSync.subscribe({
            meetingId: () => this.meeting().id,
            entityTypes: [MeetingEntityTypes.ToDo],
            isEditing: () => this.isCellEditing,
            onRefresh: () => this.refreshTodos(true),
            onDeferredRefresh: () => {
                this.pendingContentSync = true
            },
        })
    }

    ngOnDestroy(): void {
        this.contentSyncSub?.unsubscribe()
        this.grid.itemAdded.off(this.handleItemAdded.bind(this))
    }

    handleItemAdded() {
        // The user could have added multiple users, so we need to reload the ToDos to ensure the extra ToDos are loaded
        this.refreshTodos()
    }

    refreshTodos(silent = false) {
        const params = new HttpParams().set('meetingId', this.meeting().id)
        this.cancelableRequest(
            'ToDosRefresh',
            !silent,
            this.services.data
                .load(
                    !silent,
                    !silent,
                    this.services.data.toDos.queryAllByAction('GetByMeetingId', params),
                    this.services.data.users.queryAllByAction('GetForLookup'),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.grid.items = result[0]
                        this.allUsers = result[1]
                        this.allUserMap = new DataMap(this.allUsers, 'id', 'displayName')

                        this.allActiveUsers = this.allUsers.filter(user => user.active)
                        this.allActiveUserList = this.allActiveUsers.map(user => new ListItem(user.id, user.displayName))

                        //const meetingUsers = this.allActiveUsers.filter(user => this.mee)
                    },
                }),
        )
    }

    beginningEdit(): void {
        this.isCellEditing = true
    }

    gridItemFormatter = (panel: GridPanel, r, c, cell: HTMLDivElement) => {
        if (panel.cellType === CellType.Cell) {
            const col = panel.grid.columns[c] as any
            const row = panel.grid.rows[r]
            const dataItem = row.dataItem as ToDo

            // Reset cell class and style due to cell recycling
            cell.classList.remove('yellow-cell')
            cell.style.textDecoration = 'unset'

            if (col.binding === 'doneDate' || col.binding === 'dueDate' || col.binding === 'name') cell.classList.add('yellow-cell')

            if (dataItem && dataItem.doneDate != null) {
                cell.style.textDecoration = 'line-through'
            }

            if (col.binding === 'userId') {
                const originalVal = cell.innerHTML
                cell.title = originalVal
                cell.innerHTML = getInitials(originalVal)
            }

            if (col.binding == 'isHot') {
                cell.style.color = dataItem.isHot ? 'red' : 'lightgray'
                if (dataItem.isHot) {
                    cell.children[0].children[0].children[0].classList.add('fa-beat-fade')
                } else {
                    cell.children[0].children[0].children[0].classList.remove('fa-beat-fade')
                }
            }
        }
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        if (args.cancel) {
            this.isCellEditing = false
            return
        }
        this.updateRecords([flexGrid.rows[args.row].dataItem])
        this.isCellEditing = false
        if (this.pendingContentSync) {
            this.pendingContentSync = false
            this.refreshTodos(true)
        }
    }

    updateRecords(items: any[]) {
        this.services.data.toDos.edit(items[0]).subscribe({
            next: () => {
                this.grid.refresh()
            },
            error: () => {
                this.services.notification.error('The to-do changes were not successfully saved')
            },
        })
    }

    showSelectionModal() {
        const ref = this.services.modals.open(MeetingItemsSelectionComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as MeetingItemsSelectionComponent
        instance.itemType = 'To-Dos'
        instance.meetingId = this.meeting().id
        instance.itemIds = this.grid.items.map(x => x.id)
        ref.result.then(
            () => {
                this.refreshTodos()
            },
            () => {
                console.log('dismissed')
            },
        )
    }

    recurringTooltip(item: ToDo) {
        const week = this.weekOfMonthLabels[(item.recurrenceWeekOfMonth ?? 0) - 1]
        const day = this.dayOfWeekLabels[item.recurrenceDayOfWeek ?? -1]

        return week && day ? `Repeats monthly on the ${week} ${day}` : 'Repeats monthly'
    }

    toggleHot(cell) {
        if (this.canEdit) {
            cell.item.isHot = !cell.item.isHot
            this.grid.refresh()
            this.updateRecords([cell.item])
        } else {
            this.services.notification.info('You do not have permissions to change this field')
        }
    }
}
